import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  selectCalcProperties,
  selectCaseDisplayGroups,
  selectCaseProperties,
  selectLensEditorFormFields,
  selectLensFormPages,
  selectLensFormSections,
  updateCaseDisplayGroupLocations,
} from "redux/slices/lensEditor";
import { GROUP_LOCATION_WHERE, useGroupLocations } from "./useGroupLocations";
import { Box, Button, Card, IconButton, SxProps, Theme } from "@mui/material";
import { P } from "theme/typography";
import MetricsToolbar from "components/MetricsToolbar";
import {
  CaseDisplayGroup,
  CaseDisplayGroupLocationInput,
  CaseDisplayGroupLocationView,
  CaseDisplayGroupType,
} from "generated/graphql";
import { GroupTable } from "routes/lenses/lens/case/CaseDisplayGroups";
import theme from "theme";
import { DropdownButton } from "components/DropdownButton";
import { useSnackbar } from "notistack";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { swapElements } from "helpers/drag-and-drop";
import { DragIndicator } from "@mui/icons-material";
import { useFindSchemaForCaseDisplayGroupItem } from "./useFindSchemaForCaseDisplayGroupItem";
import { CASE_DISPLAY_GROUP_DEFAULT_NAME } from "./case-display-groups.loader";
import { Fragment } from "react/jsx-runtime";
import { Link } from "react-router-dom";
import { AppRoutes } from "AppRoutes";

function LocationAndGroups({
  where,
  id,
  title,
  groups,
  lensId,
  titleSX,
}: {
  where: GROUP_LOCATION_WHERE;
  id?: number;
  title: string;
  groups: CaseDisplayGroup[];
  lensId: number;
  titleSX?: SxProps<Theme>;
}) {
  const caseDisplayGroups = useAppSelector(selectCaseDisplayGroups);
  const groupLocations = useGroupLocations(caseDisplayGroups);
  const whereLocations =
    where === "CaseHome"
      ? groupLocations.caseHome
      : where === "CasesFilter"
        ? groupLocations.casesFilter
        : where === "CaseInfo"
          ? groupLocations.caseInfo
          : where === "Field" && id !== undefined
            ? groupLocations.fields[id] ?? []
            : where === "Section" && id !== undefined
              ? groupLocations.sections[id] ?? []
              : [];
  const locationInput: CaseDisplayGroupLocationInput = {
    caseHome: where === "CaseHome",
    caseInfo: where === "CaseInfo",
    casesFilter: where === "CasesFilter",
    fieldId: where === "Field" ? id : null,
    sectionId: where === "Section" ? id : null,
  };
  const availableToAddCaseDisplayGroups = caseDisplayGroups.filter(
    (cdg) =>
      cdg.name !== CASE_DISPLAY_GROUP_DEFAULT_NAME &&
      !cdg.locations.find((l) => {
        if (where === "CaseInfo") {
          return l.caseInfo === true;
        } else if (where === "CaseHome") {
          return l.caseHome === true;
        } else if (where === "CasesFilter") {
          return l.casesFilter === true;
        } else if (where === "Section") {
          return l.sectionId === id;
        } else if (where === "Field") {
          return l.fieldId === id;
        } else {
          return false;
        }
      })
  );
  const casePropertySchemas = useAppSelector(selectCaseProperties);
  const calcSchemas = useAppSelector(selectCalcProperties);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const findItemSchema = useFindSchemaForCaseDisplayGroupItem(calcSchemas, casePropertySchemas);

  const addGroup = async (group: CaseDisplayGroup) => {
    try {
      if (whereLocations !== undefined) {
        await dispatch(
          updateCaseDisplayGroupLocations({
            lensId,
            rankedGroups: whereLocations.map((wl) => wl.group.id).concat([group.id]),
            location: locationInput,
          })
        );
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Unable to add group to location", { variant: "error" });
    }
  };

  const removeGroup = async (group: CaseDisplayGroup) => {
    try {
      if (whereLocations !== undefined) {
        await dispatch(
          updateCaseDisplayGroupLocations({
            lensId,
            rankedGroups: whereLocations
              .filter((wl) => {
                const caseHomeFound = where === "CaseHome" ? wl.location.caseHome : true;
                const casesFilterFound = where === "CasesFilter" ? wl.location.casesFilter : true;
                const caseInfoFound = where === "CaseInfo" ? wl.location.caseInfo : true;
                const collectFound = where === "Collect" ? wl.location.collect : true;
                const fieldIdFound = where === "Field" ? wl.location.fieldId === id : true;
                const sectionIdFound = where === "Section" ? wl.location.sectionId === id : true;

                return !(
                  wl.group.id === group.id &&
                  caseHomeFound &&
                  casesFilterFound &&
                  caseInfoFound &&
                  collectFound &&
                  fieldIdFound &&
                  sectionIdFound
                );
              })
              .map((l) => l.group.id),
            location: locationInput,
          })
        );
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Unable to remove group", { variant: "error" });
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination || whereLocations === undefined) {
      return;
    }
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (result.type === "group") {
      const reorderedLocations = swapElements<CaseDisplayGroupLocationView>(
        whereLocations.map((wl) => wl.location),
        sourceIndex,
        destIndex
      );
      dispatch(
        updateCaseDisplayGroupLocations({
          lensId,
          rankedGroups: reorderedLocations.map((l) => l.caseDisplayGroupId),
          location: locationInput,
        })
      );
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <P variant="baseBold" sx={titleSX}>
        {title}
      </P>
      <Box sx={{ display: "flex", flexDirection: "column", pl: 2, mt: 1 }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={`droppable-${where}-${id}`} type="group">
            {(provided) => (
              <div ref={provided.innerRef}>
                {groups.map((group, groupIndex) => {
                  const metrics = group.items.map((item) => ({
                    ...item,
                    title: findItemSchema(item)?.displayName ?? "",
                    value: "-",
                  }));
                  return (
                    <Draggable key={group.id} index={groupIndex} draggableId={group.id.toString()}>
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.draggableProps}>
                          <Card
                            variant="outlined"
                            sx={{ display: "flex", flexDirection: "column", gap: 1, p: 2, mb: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                borderBottom: `1px solid ${theme.palette.grey[50]}`,
                              }}>
                              <IconButton size="small" sx={{ mr: 2 }}>
                                <span {...provided.dragHandleProps}>
                                  <DragIndicator />
                                </span>
                              </IconButton>
                              <Link to={AppRoutes.lensEditorCasePropertiesGroup(lensId, group.name)}>
                                <P variant="smSemiBold">{group.name}</P>
                              </Link>
                              <Box sx={{ flexGrow: 1 }} />
                              <Button variant="text" color="error" size="small" onClick={() => removeGroup(group)}>
                                Remove
                              </Button>
                            </Box>
                            {group.type === CaseDisplayGroupType.Card ? (
                              <MetricsToolbar metrics={metrics} />
                            ) : (
                              <GroupTable
                                columns={group.columns ?? []}
                                rows={group.rows ?? []}
                                metrics={metrics}
                                columnDescriptions={group.columnDescriptions ?? []}
                                rowDescriptions={group.rowDescriptions ?? []}
                              />
                            )}
                          </Card>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <DropdownButton<number>
          title="Add Group"
          onSelect={(groupId) => {
            const group = availableToAddCaseDisplayGroups.find((group) => group.id === groupId);
            if (group !== undefined) {
              addGroup(group);
            }
          }}
          options={availableToAddCaseDisplayGroups.map((group) => ({
            title: group.name,
            value: group.id,
          }))}
        />
      </Box>
    </Box>
  );
}

export default function GroupLocations() {
  const { lensId } = useParamNumbers(["lensId"]);
  const caseDisplayGroups = useAppSelector(selectCaseDisplayGroups);
  const displayGroupLocations = useGroupLocations(caseDisplayGroups);
  const sections = useAppSelector(selectLensFormSections);
  const pages = useAppSelector(selectLensFormPages);
  const fields = useAppSelector(selectLensEditorFormFields);

  if (lensId === undefined) {
    return <></>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
      <LocationAndGroups
        where="CaseInfo"
        title="Case Info"
        groups={displayGroupLocations.caseInfo.map((location) => location.group)}
        lensId={lensId}
        titleSX={{ textDecoration: "underline" }}
      />
      <LocationAndGroups
        where="CaseHome"
        title="Case Home"
        groups={displayGroupLocations.caseHome.map((location) => location.group)}
        lensId={lensId}
        titleSX={{ textDecoration: "underline" }}
      />
      <LocationAndGroups
        where="CasesFilter"
        title="Case Table Filter"
        groups={displayGroupLocations.casesFilter.map((location) => location.group)}
        lensId={lensId}
        titleSX={{ textDecoration: "underline" }}
      />
      {pages.map((page) => (
        <Fragment key={page.id}>
          <P variant="lgBold" key={page.id} sx={{ textDecoration: "underline" }}>
            Page: {page.name}
          </P>
          {sections
            .filter((s) => s.formPageId === page.id)
            .map((section, sectionIndex) => (
              <Fragment key={`${section.id}-section`}>
                <LocationAndGroups
                  where="Section"
                  id={section.id}
                  title={`Section: ${sectionIndex + 1}. ${section.name}`}
                  groups={(displayGroupLocations.sections[section.id] ?? []).map((location) => location.group)}
                  lensId={lensId}
                />
                {fields
                  .filter((f) => f.sectionId === section.id)
                  .map((field) => (
                    <LocationAndGroups
                      key={field.id}
                      where="Field"
                      id={field.id}
                      title={`${field.outlinePrefix} ${field.name}`}
                      groups={(displayGroupLocations.fields[field.id] ?? []).map((location) => location.group)}
                      lensId={lensId}
                    />
                  ))}
              </Fragment>
            ))}
        </Fragment>
      ))}
    </Box>
  );
}
